import { useCallback, useEffect, useMemo, useState } from 'react';
import { DollarSign } from 'lucide-react';

import { Input, Label, Skeleton, Slider, Spinner } from '@design-system';
import { PREFERENCE_FEEDBACK_MESSAGES } from '@shared/guidedPlanningPreferences';
import { formatCurrency } from '@shared/formatCurrency';

import { cn } from '@client/lib';

import {
  useSession,
  useIsSessionComplete,
  useSessionMutations,
} from '@/features/guided-financial-planning/context';
import { PREMIUM_BUDGET_STEP_ID } from '@/features/guided-financial-planning/config';
import { PreferenceFeedback } from '@/features/guided-financial-planning/components/preference-feedback';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import {
  useGuidedPlanningQuotes,
  usePreferenceQuestionProgress,
  usePremiumAmountInput,
} from '@/features/guided-financial-planning/hooks';
import {
  applyPaymentPreferenceToPremiumBounds,
  snapPremiumToStep,
  tryPremiumBoundsFromQuotes,
} from '@/features/guided-financial-planning/utils/premium-bounds-from-quotes';
import { useWizard } from 'react-use-wizard';

const PREMIUM_BOUNDS_BY_PREFERENCE: Record<
  PaymentPreference,
  { min: number; max: number; defaultValue: number }
> = {
  'upfront': { min: 25000, max: 500000, defaultValue: 100000 },
  'balanced': { min: 5000, max: 50000, defaultValue: 10000 },
  'monthly': { min: 100, max: 5000, defaultValue: 500 },
  'no-preference': { min: 10000, max: 500000, defaultValue: 100000 },
};

const STATIC_STEP = 1000;

export function PreferencesPremiumStep() {
  const { session } = useSession();
  const isSessionComplete = useIsSessionComplete();
  const { updateStepData, isSaving } = useSessionMutations();
  const { nextStep, previousStep } = useWizard();
  const { questionNumber, questionTotal, activeDotIndex } =
    usePreferenceQuestionProgress(PREMIUM_BUDGET_STEP_ID);

  const clientId = session?.clientId ?? '';
  const { data: quotes, isPending: isQuotesPending } = useGuidedPlanningQuotes(clientId, session);

  const paymentPreference = session?.stepData?.preferences?.paymentPreference ?? 'upfront';
  const sessionTargetPremium = session?.stepData?.targetPremium;

  const expectsQuotesFetch = Boolean(
    clientId && session?.id && session?.stepData?.selectedPolicyCategories?.length,
  );

  const showQuotesLoading = expectsQuotesFetch && isQuotesPending && !isSessionComplete;

  const quoteDerivedBounds = useMemo(() => tryPremiumBoundsFromQuotes(quotes), [quotes]);

  const bounds = useMemo(() => {
    if (quoteDerivedBounds) {
      return applyPaymentPreferenceToPremiumBounds(quoteDerivedBounds, paymentPreference);
    }
    const staticBounds = PREMIUM_BOUNDS_BY_PREFERENCE[paymentPreference];
    return { ...staticBounds, step: STATIC_STEP };
  }, [quoteDerivedBounds, paymentPreference]);

  const [targetPremium, setTargetPremium] = useState<number>(() => {
    if (typeof sessionTargetPremium === 'number') {
      const c = Math.max(bounds.min, Math.min(bounds.max, sessionTargetPremium));
      return snapPremiumToStep(c, bounds.min, bounds.max, bounds.step);
    }
    return snapPremiumToStep(bounds.defaultValue, bounds.min, bounds.max, bounds.step);
  });

  useEffect(() => {
    if (typeof sessionTargetPremium === 'number') {
      const c = Math.max(bounds.min, Math.min(bounds.max, sessionTargetPremium));
      setTargetPremium(snapPremiumToStep(c, bounds.min, bounds.max, bounds.step));
    }
  }, [sessionTargetPremium, bounds.min, bounds.max, bounds.step]);

  useEffect(() => {
    setTargetPremium(prev => snapPremiumToStep(prev, bounds.min, bounds.max, bounds.step));
  }, [bounds.min, bounds.max, bounds.step]);

  const premiumFeedbackMessage = PREFERENCE_FEEDBACK_MESSAGES.premiumBudget[paymentPreference];

  const clampedValue = Math.max(bounds.min, Math.min(bounds.max, targetPremium));
  const sliderValue = snapPremiumToStep(clampedValue, bounds.min, bounds.max, bounds.step);

  const {
    commitDraft,
    handlePremiumInputChange,
    handlePremiumInputFocus,
    inputDisplayValue,
    clearAmountDraft,
  } = usePremiumAmountInput({
    clampedValue,
    min: bounds.min,
    max: bounds.max,
    step: bounds.step,
    onPersist: setTargetPremium,
  });

  const savePremium = useCallback(async () => {
    if (isSessionComplete) return;
    const toSave = commitDraft();
    if (toSave !== session?.stepData?.targetPremium) {
      await updateStepData({ targetPremium: toSave });
    }
  }, [commitDraft, isSessionComplete, session?.stepData?.targetPremium, updateStepData]);

  const handleNext = useCallback(async () => {
    await savePremium();
    nextStep();
  }, [savePremium, nextStep]);

  const handleBack = useCallback(async () => {
    await savePremium();
    previousStep();
  }, [savePremium, previousStep]);

  const subtitle = isSessionComplete
    ? 'This plan is view-only. The target premium below is from when the plan was finalized.'
    : "Use the slider or type an amount you’re comfortable investing in coverage. The range is based on policies that match your selections.";

  return (
    <StepLayout
      title="How much are you considering budgeting in premium?"
      subtitle={subtitle}
      icon={
        <div className="flex flex-col items-center gap-3">
          <span className="text-sm font-medium text-gray-500">
            Question {questionNumber} of {questionTotal}
          </span>
          <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
            <DollarSign className="text-primary size-7" />
          </div>
        </div>
      }
      nextButtonLabel="Continue"
      isNextDisabled={isSaving || showQuotesLoading}
      onNext={handleNext}
      onBack={handleBack}
      isLoading={isSaving}
    >
      <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6 px-2">
        {showQuotesLoading ? (
          <div className="flex w-full flex-col items-center gap-6 py-8">
            <Spinner className="text-primary size-10" />
            <p className="text-muted-foreground text-center text-sm">
              Loading policy options to set your premium range…
            </p>
            <div className="flex w-full flex-col">
              <div className="mx-auto flex w-full max-w-sm flex-col space-y-2">
                <Skeleton className="mx-auto h-8 w-40" />
                <Skeleton className="mx-auto h-14 w-full rounded-md" />
                <div className="flex justify-between gap-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
              <div className="mt-8 space-y-4">
                <Skeleton className="h-2 w-full rounded-full" />
                <div className="flex justify-between gap-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex w-full flex-col">
              <div className="mx-auto w-full max-w-sm space-y-2 text-center">
                <Label
                  htmlFor="guided-planning-target-premium"
                  className="text-muted-foreground text-sm font-normal"
                >
                  Target premium
                </Label>
                <div className="relative">
                  <span
                    className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl font-medium"
                    aria-hidden
                  >
                    $
                  </span>
                  <Input
                    id="guided-planning-target-premium"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={isSessionComplete}
                    value={inputDisplayValue}
                    onChange={handlePremiumInputChange}
                    onFocus={handlePremiumInputFocus}
                    onBlur={() => {
                      commitDraft();
                    }}
                    className="text-primary min-h-14 py-2.5 pl-10 text-center text-2xl font-semibold leading-tight tracking-tight md:text-2xl"
                    aria-describedby="guided-planning-premium-range-hint"
                  />
                </div>
                <div
                  id="guided-planning-premium-range-hint"
                  className="text-muted-foreground flex w-full justify-between text-sm"
                >
                  <span className="text-left">Min {formatCurrency(bounds.min)}</span>
                  <span className="text-right">Max {formatCurrency(bounds.max)}</span>
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <Slider
                  value={[sliderValue]}
                  onValueChange={nextValues => {
                    clearAmountDraft();
                    const raw = nextValues[0] ?? sliderValue;
                    setTargetPremium(snapPremiumToStep(raw, bounds.min, bounds.max, bounds.step));
                  }}
                  min={bounds.min}
                  max={bounds.max}
                  step={bounds.step}
                  disabled={isSessionComplete}
                />

                <div className="text-muted-foreground flex items-center justify-between text-sm">
                  <span>{formatCurrency(bounds.min)}</span>
                  <span>{formatCurrency(bounds.max)}</span>
                </div>
              </div>

              <div className="mt-4 w-full">
                <PreferenceFeedback message={premiumFeedbackMessage} />
              </div>
            </div>

            <div className="mt-2 flex justify-center gap-2">
              {Array.from({ length: questionTotal }, (_, i) => (
                <div
                  key={i}
                  className={cn(
                    'size-2 rounded-full',
                    i === activeDotIndex ? 'bg-primary' : 'bg-gray-300',
                  )}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </StepLayout>
  );
}
